import {
  HttpContentService,
  KernelService,
  Sandbox,
  SessionService,
  JupyterKernelClient,
  SessionInfo,
  KernelEvent,
  PreludeAction
} from '@sandbox/api-sdk';
import { SandboxContainer, createContainer } from './SandboxContainer';
import store from '@/store';
import axios from 'axios';

import Path from 'path';
export * from './types';
import { Type, File, FileLoadState, GetRawPayload, MimeTypes, Exts } from './types';
import { download as downloadAction, checkIsBinary } from './utils';

const cacheFiles: { [key: string]: File } = {};
let sandboxContainer: SandboxContainer | null = null;

const formatBindKey = (prefix: string, path: string) => {
  return `${prefix}_${path}`;
};

export async function bindJupyterContainer(path: string) {
  const container = await getContainer();
  container.bind(formatBindKey('KernelService', path), async (get) => {
    const sandbox = await get(Sandbox);
    return new KernelService(sandbox);
  });
  container.bind(formatBindKey('JupyterKernelClient', path), async (get) => {
    const [sandbox, kernelService] = await Promise.all([
      get(Sandbox),
      get(formatBindKey('KernelService', path))
    ]);
    return new JupyterKernelClient(sandbox, kernelService);
  });
  container.bind(formatBindKey('SessionService', path), async (get) => {
    const sandbox = await get(Sandbox);
    return new SessionService(sandbox);
  });
}

export async function createSessionService(params: {
  path: string;
  name: string;
}): Promise<SessionInfo> {
  const container = getContainer();

  let sessionService: SessionService | null = null;
  const path = params.path;
  try {
    sessionService = await container.get(formatBindKey('SessionService', path));
  } catch (error) {
    if (!sessionService) {
      await bindJupyterContainer(path);
      sessionService = await container.get(formatBindKey('SessionService', path));
    }
  }
  return sessionService!.create(params);
}

export async function getKernelClient(params: { path: string; name: string }) {
  const container = getContainer();
  const sessionInfo = await createSessionService(params);
  const kernelClient = await container.get(formatBindKey('JupyterKernelClient', params.path));
  return { kernel: kernelClient, start: () => kernelClient.start(sessionInfo.kernel.id) };
}

// export async function kernalRestart(kernel: JupyterKernelClient, cbEvent: Func<any>) {
//   kernel?.off(KernelEvent.Restarting, cbEvent);
//   await kernel?.restart();
//   kernel?.on(KernelEvent.Restarting, cbEvent);
// }

export function getContainer(): SandboxContainer {
  const config = ((store.state as any)?.sandbox as any)?.sandboxConfig;
  if (!config?.token) {
    throw Error('申请沙盒token不能为空');
  }
  if (!sandboxContainer) {
    sandboxContainer = createContainer(config);
  }
  return sandboxContainer;
}

export async function getContentService(type: Type): Promise<HttpContentService> {
  if (!type) {
    throw Error('申请沙盒类型不能为空');
  }
  return getContainer().getContentService();
}

export async function saveFile(file: File, type: Type = Type.normal): Promise<File> {
  if (cacheFiles[file.path]?.content === file.content) return file;
  const service = await getContentService(type);
  const { isSelected, ...args } = file;
  cacheFiles[file.path] = { ...args, isSelected: false, isDeleted: false };
  return service.save(file.path, file.content);
}

// eslint-disable-next-line max-params
export async function getFile(
  file: File,
  type: Type = Type.normal,
  widthPending = false,
  preludeFiles: string[] = [],
  isForce = false
): Promise<[(() => Promise<File>) | null, File] | File> {
  const cacheFile = cacheFiles[file.path];
  if (cacheFile && !isForce) {
    if (widthPending) {
      return [null, JSON.parse(JSON.stringify(cacheFile))];
    }
    return Promise.resolve([
      () => Promise.resolve(JSON.parse(JSON.stringify(cacheFile))),
      JSON.parse(JSON.stringify(cacheFile))
    ]);
  }
  const module: File = { ...file, loadState: FileLoadState.pending, isDeleted: false };
  const service = await getContentService(type);
  const fetchFunc = (): Promise<File> =>
    service
      .get(file.path)
      .then((s) => {
        const res = {
          ...module,
          downloadUrl: service.makeDownloadUrl(s.path),
          loadState: FileLoadState.fulfilled,
          ...s
        };
        if (preludeFiles?.includes(module.path)) {
          res.isFromPrelude = true;
        }
        if (!s.isDir) {
          cacheFiles[s.path] = res;
        }
        return res;
      })
      .catch((e) => {
        if (e?.response?.status === 404) {
          const res = { ...module, loadState: FileLoadState.fulfilled, isDeleted: true };
          cacheFiles[file.path] = res;
          return res;
        }
        return { ...module, loadState: FileLoadState.rejected };
      });

  if (widthPending) {
    return [fetchFunc, module];
  }
  return fetchFunc();
}

export async function getFolder(
  module: File,
  type: Type = Type.normal,
  preludeFiles: string[] = []
) {
  const getSubFolder = async (subModule: File) => {
    try {
      if (subModule.isDir) {
        const res = (await getFile(subModule, type, false, preludeFiles)) as File;
        res.content = await Promise.all((res.content as File[]).map((item) => getSubFolder(item)));
        return res;
      } else {
        const service = await getContentService(type);
        subModule.downloadUrl = service.makeDownloadUrl(subModule.path);
        if (preludeFiles.includes(subModule.path)) {
          subModule.isFromPrelude = true;
        }
      }
      return subModule;
    } catch (error) {
      return subModule;
    }
  };
  return await getSubFolder(module);
}

export function setCacheFile(file: File) {
  cacheFiles[file.path] = file;
}

export function updateCacheFile(
  file: File,
  type = Type.normal,
  preludeFiles: string[] = []
): Promise<File[]> | null {
  let keys = cacheFiles[file.path] ? [file.path] : [];
  if (file.isDir) {
    keys = Object.keys(cacheFiles).filter((item) => item.includes(file.path));
  }
  if (keys.length) {
    return Promise.all(
      keys.map(
        (path) => getFile(cacheFiles[path], type, false, preludeFiles, true) as Promise<File>
      )
    );
  }
  return null;
}

export async function getRaw(data: GetRawPayload, type = Type.normal): Promise<BinaryType> {
  const contentService = await getContentService(type);
  return contentService.getRaw(data.path, { responseType: data.responseType }) as any;
}

// 文件预置
export async function prelude(
  data: { action: PreludeAction; rootPath: string; path: string; source: string },
  type = Type.normal
) {
  const contentService = await getContentService(type);
  const res = await contentService?.prelude({
    action: data.action,
    source: data.source,
    source_path: data.rootPath, // Path.normalize(Path.relative(data.rootPath, data.path))
    target: data.path
  });
  if (cacheFiles[res.path]) {
    delete cacheFiles[res.path];
  }
  return res;
}
// todo
// export async function filePrelude(
//   data: { action: PreludeAction; rootPath: string; path: string; source: string },
//   type = Type.normal
// ) {
//   const contentService = await getContentService(type);
//   const res = await contentService?.prelude({
//     action: data.action,
//     source: data.source,
//     source_path: Path.normalize(Path.relative(data.rootPath, data.path)),
//     target: data.path
//   });
//   if (cacheFiles[res.path]) {
//     delete cacheFiles[res.path];
//   }
//   return res;
// }

// export async function download(file: File) {
//   return downloadAction({ url: file.downloadUrl, name: file.name });
// }

export async function download(file: File, type = Type.normal) {
  const contentService = await getContentService(type);
  const downloadUrl = await axios
    .get(file.downloadUrl, { responseType: 'arraybuffer' })
    .then((res: any) => {
      const data = res.data;
      const ext = (file.isDir ? 'zip' : Path.extname(file.path).replace(/^./, '')) as Exts;
      let downloadFile: any[] = [data];
      if (!checkIsBinary(ext)) {
        /** fix 下载下来的文件乱码 */
        downloadFile = [new Uint8Array([0xef, 0xbb, 0xbf]), data];
      }
      const blob = new Blob(downloadFile, { type: `${MimeTypes[ext || 'txt']},charset=UTF-8` });
      return (window.URL || window.webkitURL).createObjectURL(blob);
    });
  return downloadAction({ url: downloadUrl, name: file.name });
}
